import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchDriver, FetchYardstikReport } from 'src/constants/actionTypes'
import { usePolling } from 'src/utils/hooks/usePolling'
import { YardstikReport } from 'src/interfaces/yardstik'
import tw, { css } from 'twin.macro'
import { SvgIcon } from '@mui/material'
import { CancelMajor } from '@shopify/polaris-icons'
import { handleApiError } from 'src/utils/errorHandlers'
import { Driver } from 'src/interfaces/driver'
import { CollectDataStepStatus, CollectDataStepType } from 'src/constants/collect-data/enums'

const YARDSTIK_VIEW_DRIVER_UPDATE_POLLING_INTERVAL = 1000 * 5
const DRIVER_UPDATE_POLLING_INTERVAL = 1000 * 30

export const useYardstik = () => {
  const dispatch = useDispatch()

  const [yardstikReport, setYardstikReport] = useState<YardstikReport | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const startYardstik = useCallback(async (stepId: string) => {
    try {
      setIsLoading(true)

      const report: YardstikReport | undefined = await createAsyncAction(
        dispatch,
        FetchYardstikReport.request({
          stepId,
        }),
      )

      if (report) {
        setYardstikReport(report)
      }
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const removeYardstikReport = useCallback(() => {
    setYardstikReport(null)
  }, [])

  const handleUpdateDriver = useCallback(async (stopPolling: () => void) => {
    const driver: Driver | null = await createAsyncAction(dispatch, FetchDriver.request())

    if (
      driver?.collectDataStepsConfigs?.some((item) =>
        item.collectDataSteps.some(
          (step) =>
            step.type === CollectDataStepType.BGC && step.status !== CollectDataStepStatus.Created,
        ),
      )
    ) {
      stopPolling()
    }
  }, [])

  const { startPolling: yardstikViewStartPolling, stopPolling: yardstikViewStopPolling } =
    usePolling(handleUpdateDriver, YARDSTIK_VIEW_DRIVER_UPDATE_POLLING_INTERVAL)

  useEffect(() => {
    if (yardstikReport) {
      yardstikViewStartPolling()
    } else {
      yardstikViewStopPolling()
    }
  }, [yardstikReport])

  const { startPolling: pageRefreshStartPolling, stopPolling: pageRefreshStopPolling } = usePolling(
    handleUpdateDriver,
    DRIVER_UPDATE_POLLING_INTERVAL,
  )

  useEffect(() => {
    pageRefreshStartPolling()

    return () => {
      pageRefreshStopPolling()
    }
  }, [])

  const YardstikView = useMemo(
    () => () =>
      yardstikReport ? (
        <div css={[tw`fixed w-full h-full top-0 left-0 z-20`, styles.yardstikViewContainer]}>
          <iframe css={tw`w-full h-full border-0`} src={yardstikReport.applyLink} />
          <SvgIcon
            css={tw`absolute right-5 top-5 w-5 h-5 z-20 cursor-pointer`}
            onClick={removeYardstikReport}
          >
            <CancelMajor css={tw`text-shades-400`} />
          </SvgIcon>
        </div>
      ) : null,
    [yardstikReport],
  )

  return {
    isYardstikLoading: isLoading,
    startYardstik,
    YardstikView,
  }
}

const styles = {
  yardstikViewContainer: css`
    background-color: var(--mui-shades-0);
  `,
}
